import React, { useEffect, useRef, useState } from "react";
import ImagePreviewStrip from "../../create_page/ImagePreviewStrip";
import MessageImages from "../../messaging_page/chat_frame/MessageImages";
import {
  MAX_CONTENT_IMAGES,
  appendImageFiles,
  collectImageFiles,
  uploadImageFile,
} from "../../../lib/contentImages";
import QuestionFrame from "./QuestionFrame";

/**
 * @param {unknown} value
 * @returns {Array<{id: string, preview: string, url: string, key: string}>}
 */
function itemsFromAnswer(value) {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item) => item && typeof item.url === "string" && typeof item.key === "string")
    .map((item) => ({
      id: item.key,
      preview: item.url,
      url: item.url,
      key: item.key,
    }));
}

/**
 * @param {Array<{url?: string, key?: string}>} items
 * @returns {Array<{url: string, key: string}>}
 */
function uploadedAnswer(items) {
  return items
    .filter((item) => item.url && item.key)
    .map((item) => ({ url: item.url, key: item.key }));
}

/**
 * Image attachments using the same thumbnail strip as posts.
 *
 * @param {Object} props
 * @param {object} props.question
 * @param {unknown} [props.value]
 * @param {function(unknown, {{ immediate?: boolean, uploading?: boolean }}): void} props.onChange
 * @param {string} [props.error]
 * @param {boolean} [props.readOnly]
 * @param {boolean} [props.showRequired]
 * @returns {JSX.Element}
 */
export default function ImageQuestion({ question, value, onChange, error, readOnly, showRequired }) {
  const fileInputRef = useRef(null);
  const itemsRef = useRef([]);
  const [items, setItems] = useState(() => itemsFromAnswer(value));
  const [localError, setLocalError] = useState("");
  const [uploading, setUploading] = useState(false);
  const maxFiles =
    Number.isFinite(question?.maxFiles) && question.maxFiles > 0
      ? question.maxFiles
      : MAX_CONTENT_IMAGES;

  itemsRef.current = items;

  useEffect(() => {
    if (itemsRef.current.some((item) => item.file && !item.url)) return;
    setItems(itemsFromAnswer(value));
  }, [value]);

  useEffect(() => {
    return () => {
      itemsRef.current.forEach((item) => {
        if (item.file && item.preview?.startsWith("blob:")) {
          URL.revokeObjectURL(item.preview);
        }
      });
    };
  }, []);

  /**
   * @param {Array<{id: string, preview: string, url?: string, key?: string, file?: File}>} next
   * @returns {void}
   */
  function commit(next) {
    const busy = next.some((item) => item.file && !item.url);
    itemsRef.current = next;
    setItems(next);
    setUploading(busy);
    onChange(uploadedAnswer(next), { immediate: true, uploading: busy });
  }

  /**
   * @param {File[]|FileList} files
   * @returns {Promise<void>}
   */
  async function addImageFiles(files) {
    const selected = collectImageFiles(files);
    if (!selected.length) {
      if (files && (files.length || files.files?.length)) {
        setLocalError("Please select an image file");
      }
      return;
    }
    const { items: next, error: addError } = appendImageFiles(selected, itemsRef.current, maxFiles);
    setLocalError(addError);
    const pending = next.filter((item) => item.file && !item.url);
    commit(next);
    if (!pending.length) return;
    let working = next;
    for (const item of pending) {
      try {
        const uploaded = await uploadImageFile(item.file);
        if (item.preview?.startsWith("blob:")) URL.revokeObjectURL(item.preview);
        working = working.map((entry) =>
          entry.id === item.id
            ? { id: uploaded.key, preview: uploaded.url, url: uploaded.url, key: uploaded.key }
            : entry
        );
        commit(working);
      } catch (err) {
        if (item.preview?.startsWith("blob:")) URL.revokeObjectURL(item.preview);
        working = working.filter((entry) => entry.id !== item.id);
        setLocalError(err.message || "Failed to upload image");
        commit(working);
      }
    }
  }

  /**
   * @param {string} id
   * @returns {void}
   */
  function removeImage(id) {
    const found = itemsRef.current.find((item) => item.id === id);
    if (found?.file && found.preview?.startsWith("blob:")) {
      URL.revokeObjectURL(found.preview);
    }
    commit(itemsRef.current.filter((item) => item.id !== id));
  }

  /**
   * @param {string} fromId
   * @param {string} toId
   * @returns {void}
   */
  function reorderImages(fromId, toId) {
    const prev = itemsRef.current;
    const from = prev.findIndex((item) => item.id === fromId);
    const to = prev.findIndex((item) => item.id === toId);
    if (from < 0 || to < 0 || from === to) return;
    const next = [...prev];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    commit(next);
  }

  const fieldId = `survey-q-${question.id}`;
  const previewUrls = items.map((item) => item.preview || item.url).filter(Boolean);

  if (readOnly) {
    return (
      <QuestionFrame question={question} error={error || localError} showRequired={showRequired}>
        {previewUrls.length ? <MessageImages urls={previewUrls} /> : (
          <p className="text-sm text-text-muted">No images</p>
        )}
      </QuestionFrame>
    );
  }

  return (
    <QuestionFrame question={question} error={error || localError} showRequired={showRequired}>
      <input
        ref={fileInputRef}
        id={fieldId}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        disabled={uploading}
        onChange={(event) => {
          addImageFiles(event.target.files || []);
          event.target.value = "";
        }}
      />
      {items.length ? (
        <ImagePreviewStrip
          items={items}
          onReorder={reorderImages}
          onRemove={removeImage}
          onAdd={() => fileInputRef.current?.click()}
          disabled={uploading}
          maxImages={maxFiles}
        />
      ) : null}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || items.length >= maxFiles}
          aria-label="Add photo"
          title="Add photo"
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-ui-border bg-ui-card text-text-primary transition hover:bg-highlight focus:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth="2" />
            <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" stroke="none" />
            <path strokeWidth="2" d="M21 15l-5-5L5 21" />
          </svg>
        </button>
        {uploading ? <span className="text-sm text-text-secondary">Uploading…</span> : null}
      </div>
    </QuestionFrame>
  );
}
